export interface BlogPost {
   slug: string
   title: string
   metaDescription: string
   content: string
}

export const blogPosts: BlogPost[] = [
   {
      slug: 'why-does-adding-spaces-help-in-reading-thai',
      title: 'Why does adding spaces help in reading Thai?',
      metaDescription: 'Why does adding spaces help in reading Thai?',
      content: `<p>Learning to read Thai never seemed like it was going to be an easy task, and it certainly didn&#8217;t start out that way for me.</p>
<p>My textbook did its best to kill my initial enthusiasm by flatly informing me that there are no spaces between the words, and moving on to long lists of letters, rules and exceptions to memorize instead.</p>
<p>After waking up, I started to seriously wonder if this was going to be possible to learn at all. What about English without spaces, <em>wouldthatevenbepossibletoread</em>?</p>
<p>Hmm, OK, maybe that wasn&#8217;t so bad. But then in English, I already knew all the words. In Thai, I was just a beginner with a limited (=tiny) vocabulary.</p>
<p>How am I supposed to know where one word starts and another ends, if they&#8217;re words I don&#8217;t already know? But then, how can I improve my reading vocabulary without being able to read first?</p>
<p>It&#8217;s not exactly a catch-22, but it&#8217;s not far off it. If you&#8217;re learning to read Thai, chances are high that you&#8217;ve run into a similar problem too.</p>
<p>The solution is to start with short, simple and common words, and you can gradually build up from there. Before you read even a single word though, you need to get to grips with the Thai alphabet.</p>
<h2>Well, what&#8217;s difficult about the Thai alphabet anyway?</h2>
<p>First off is the size of it, with 44 consonants. Some look <em>just</em> similar enough to each other that at small font sizes you&#8217;re going to need a magnifying glass to distinguish them. A bunch of them are obscure enough that you&#8217;re going to forget them multiple times. A few have the dubious honor of being both confusingly similar and vanishingly rare.</p>
<p>The real world doesn&#8217;t make things any easier either, as &#8220;stylish&#8221; fonts used in advertising minimize differences between the letters down to practically a single pixel.</p>
<p>That&#8217;s just the consonants too. Vowels in Thai can be written before, after, above and below the consonants, and frequently in some combination of that too. So even if you were able to read non-spaced English with scanning back and forth, you won&#8217;t be able to avoid it in Thai.</p>
<h2>The method in the madness</h2>
<p>It turns out the seemingly crazy positioning of the vowels is actually your lifeline in learning to read. There are specific rules about where consonants must be positioned in relation to the vowels and tone marks, vastly reducing the potential ambiguity of where syllables start and end.</p>
<p>(At least, if we overlook the complication of inherent vowels for the moment).</p>
<p>So that mostly takes care of where the syllables are, and from there you can use simple trial-and-error to see which possible combinations of syllables result in real words. Over time, you gradually expand vocabulary, learn to recognize more and more words, and the whole process eventually becomes quicker and easier.</p>
<p>At least, that&#8217;s what I had to do.  Thai2english is intended to spare you some of this same pain!</p>
<p>We&#8217;ll automatically add spaces between the words for you.  This lets you focus instead on the more important things &#8211; remembering the letters of the alphabet, the vowel positioning, improving your reading speed and expanding your vocabulary.</p>
<p>It&#8217;s all much easier to do when you don&#8217;t also have to calculate where the words start and end. That comes almost naturally later once your vocabulary is large enough.</p>
<p>(As you get better at reading, you might grow to prefer it Thai-style with no spaces in there. We don&#8217;t have an option for turning off the automatic spaces yet, but it&#8217;s on our list.)</p>
`,
   },
   {
      slug: 'thai-transliteration-the-good-the-bad-and-the-ugly',
      title: 'Thai transliteration – the good, the bad and the ugly',
      metaDescription: 'Thai transliteration – the good, the bad and the ugly',
      content: `<p>When we say <em>transliteration</em>, we mean a way of writing Thai words using the English alphabet, rather than in Thai script. You&#8217;ll see it throughout thai2english, and it looks like this&#8230;</p>
<p><img loading="lazy" class="small-left size-full wp-image-32 alignleft" src="/img/blog/transliteration2.png" alt="transliteration2" /></p>
<p>The little accent markers above some of the letters are what we use to show the <a href="/learn/what-are-tone-markers">tone markers</a> that each syllable is pronounced with.</p>
<h2>Why we need transliteration</h2>
<p>If you&#8217;re just starting to learning Thai, then transliteration is your best friend. It lets you read, write and pronounce Thai words, without first having to spend years learning to decipher and remember the Thai alphabet.</p>
<p>This is a massive benefit, as it lets you get right to the goal of communicating with and understanding Thais much quicker.</p>
<p>Used well, transliteration lets you build up a decent-sized vocabulary which you can then use in your day-to-day conversations, and to make your life in Thailand easier.</p>
<p>Unfortunately, it&#8217;s not quite <em>that</em> easy. Once you progress a bit more in your studies, transliteration quickly starts holding you back.</p>
<h2>&#8230;and what&#8217;s wrong with it</h2>
<p>The first, and most obvious, problem is that Thais themselves don&#8217;t use it.</p>
<aside>
<header>Can Thai people read and understand transliterated Thai?</header>
<p>Yes and no – it varies widely. I’ve seen some who are remarkably good at it, and others who look at it with a look of bemusement before calling their friends over to help.</p>
<p>They mostly know transliteration as “karaoke language” (ภาษาคาราโอเกะ) as it’s on every karaoke music videos. So, good news — not being able to read Thai will not prevent you massacring the tune of Thai songs!</p>
</aside>
<p>If you want to be able to read Thai Facebook posts, web pages, Line chats, menus, newspapers or, well, anything else, then you&#8217;re gonna have to be able to read Thai script.</p>
<p>There’s a more subtle problem too. Thai contains a whole bunch of sounds that we just can’t accurately and unambiguously write using the English alphabet.</p>
<p>The best we can do is to get as close as possible. Unfortunately though, no-one can agree on the best way to do that.</p>
<p>Let&#8217;s take the Thai word <span class="thai">ข้าว</span> (rice) as an example — on restaurant menus you’ll variously see it written as <em>khao</em>, <em>cow</em>, <em>kao</em> and <em>kaaw</em> amongst others.</p>
<p>What’s the best one? Maybe I think it’s <em>kao</em>, and maybe you think it’s something else. The problem is no-one’s really right or wrong, it’s all just personal opinion. The only correct spelling is the one in Thai script.</p>
<p>So if you&#8217;re given some transliterated Thai to read, it&#8217;s anyone&#8217;s guess as to how difficult that&#8217;s going to be. If you&#8217;re lucky, they used a transliteration style and vocab that&#8217;s familiar to you, and it&#8217;s easy. Other times, it might as well be in hieroglyphics.</p>
<p>With every single syllable having multiple different ways someone could choose to spell it, you end up left with the world&#8217;s least fun guessing game. For me, this was about the time when Thai script, with its simple and unambiguous spelling, started to look like the better option.</p>
<p>Yes, at least in theory. It&#8217;s snappily known as the <em>Royal Thai General System for Transcription</em>, or RTGS to its friends.</p>
<aside>
<header>Going their own way</header>
<p>Even at an official level, use of the RTGS is surprisingly inconsistent. Exceptions are everywhere, whether due to historical, etymological or stylistic reasons.</p>
<p>These include well-known place names such as “Pattaya” and “Suvarnabhumi”, road names like &#8220;Asoke&#8221; and &#8220;Sukhumvit&#8221;, or famous surnames like “Shinawatra”.</p>
</aside>
<p>If you find yourself needing an official transliteration of a Thai place name, person&#8217;s name, name of a legal document or the like, the RTGS is what you&#8217;d turn to.</p>
<p>In practice though, the RTGS is widely ignored and many Thais are not even aware of what it is. It&#8217;s quite a contrast from Chinese students who are taught Pinyin from an early age.</p>
<p>If a Thai person finds they need to transliterate a word, they’ll just do it in an ad-hoc way based on whatever seems best to them at the time.</p>
<p>Well, erm, no.</p>
<p>Just like everyone else, we have our own opinions on what makes a good transliteration and so we have our own way of doing it.</p>
<p>The problem is the RTGS often uses the same English letters to represent very different sounds in Thai. This makes the resulting spelling pleasingly short, but next to impossible to correctly pronounce if you don&#8217;t already know how to.</p>
<p>At the other end of the scale, it&#8217;s possible to transliterate based on the symbols of the International Phonetic Alphabet (IPA) instead. This produces the most accurate transliterations, but the end result is almost as hard to read as Thai script itself (seasoned linguistic experts excepted).</p>
<p>As our goal is to help you learn to speak Thai, the thai2english transliteration is focused on giving you a consistent spelling that is both easy-to-read and possible to accurately pronounce. With over ten years of providing automatic Thai transliteration, the thai2english transliterations are now widely used and familiar to many.</p>
<h2>The bottom line</h2>
<p>Transliterated spelling will never be perfect, and is always a compromise in one way or another. It&#8217;s best to treat it a short-term aid in your learning, a kickstart that helps you progress through the early stages of learning Thai and building up your vocabulary.</p>
<p>In the longer term, it&#8217;s well worth saving yourself the headaches that transliteration can bring and learning Thai script instead. It&#8217;s not as difficult as you might think!</p>
`,
   },
   {
      slug: 'what-are-tone-markers',
      title: 'What are tone markers in Thai transliteration?',
      metaDescription: 'What are tone markers in Thai transliteration?',
      content: `<p>Like it or not (I&#8217;m not sure I do!), Thai is a tonal language and you&#8217;ll need to be able to use the correct tones when speaking. Every syllable has its own tone — either mid, low, high, falling or rising.</p>
<p>Get it wrong, and hopefully, your listener will politely ignore your mistake. If they&#8217;re not quite so polite, they may practically fall over laughing so much. Not much fun to be on the receiving end of that again and again, so it&#8217;s well worth trying to get the tones correct.</p>
<p>Glad you asked. When using Thai script, there&#8217;s a byzantine series of rules that govern the tone, but in <a href="/learn/thai-transliteration-the-good-the-bad-and-the-ugly"> transliteration</a> we have the freedom to look for an easier way.</p>
<p>Our solution is to use the accent symbols as tone markers, as they look approximately reflect the sound of the tone. We&#8217;ll add one of these above the first vowel in each symbol, except for mid-tones.</p>
<p>ToneExample</p>
<table class="tone-markers">
<thead></thead>
<tbody>
<tr>
<td>Mid</td>
<td class="tlit">mai</td>
</tr>
<tr>
<td>Low</td>
<td class="tlit">mài</td>
</tr>
<tr>
<td>High</td>
<td class="tlit">mái</td>
</tr>
<tr>
<td>Falling</td>
<td class="tlit">mâi</td>
</tr>
<tr>
<td>Rising</td>
<td class="tlit">măi</td>
</tr>
</tbody>
</table>
<p>Though these accent symbols weren&#8217;t at all intended for Thai, it turns out they&#8217;re accidentally perfect for showing the Thai tones.</p>
<p>Just like the symbols, the Thai falling tone actually rises slightly in pitch before falling, and the rising tone does the opposite. Similarly, the high and low tones actually start roughly neutral in pitch before moving up and down respectively.</p>
<p>The tone marker symbols mirror this in pitch, so as well as showing the tone they perform double duty by acting as a subtle reminder of how the tone should be pronounced too.</p>
`,
   },
   {
      slug: 'the-crazy-world-of-thai-nicknames',
      title: 'The crazy world of Thai nicknames',
      metaDescription: 'The crazy world of Thai nicknames',
      content: `<div class="blog-page-nicknames">
Just about every Thai person has a nickname by which they are known informally, given to them by their parents at birth. The pervasive use of nicknames in this way apparently comes from the old belief that evil spirits ( <span class="thai">ปีศาจ</span>, <span class="tlit">bpee-sàat</span> ) are constantly on the lookout for newborn children to snatch away and control. The spirits are apparently easily fooled though, as just the use of a nickname instead of a normal Thai name confuses them and helps to keep the child safe.</p>
<p>This is not a widespread belief nowadays of course, but nonetheless the use of nicknames remains widespread in Thailand. So much so that it&#8217;s not uncommon to know someone for years and still not be entirely sure of their real name and surname (unless it&#8217;s on their Facebook page!)</p>
<p>The nicknames given may be a contraction of their real name, but most often Thai parents take inspiration from a wide variety of other sources instead.</p>
<p>One common source of names is from your how you are at birth, including your appearance, size or whether you&#8217;re the first child. </p>
<div class="nicknames-table">
<ul>
<li>Bum</li>
<li><span class="thai">บุ๋ม</span> <span class="tlit">bŭm</span><span class="literally"> Dimpled</span></li>
<li>Daeng</li>
<li><span class="thai">แดง</span> <span class="tlit"> daeng </span><span class="literally">Red</span></li>
</ul>
<ul>
<li>Lek</li>
<li><span class="thai">เล็ก</span> <span class="tlit"> lék </span><span class="literally">Small </span></li>
<li>Dam</li>
<li><span class="thai">ดำ</span> <span class="tlit"> dam</span><span class="literally">Black</span></li>
</ul>
<ul>
<li>Nit</li>
<li><span class="thai">นิด</span> <span class="tlit"> nít </span><span class="literally">Small</span></li>
<li>Muay</li>
<li><span class="thai">หมวย</span> <span class="tlit"> mŭay </span><span class="literally">(A light-skinned female of Chinese descent)</span></li>
</ul>
<ul>
<li>Noi</li>
<li><span class="thai">น้อย</span> <span class="tlit"> nói </span><span class="literally">Small</span></li>
<li>Som</li>
<li><span class="thai">ส้ม</span> <span class="tlit"> sôm </span><span class="literally">Orange</span></li>
</ul>
<ul>
<li>Pui</li>
<li><span class="thai">ปุ้ย</span> <span class="tlit"> bpûi </span><span class="literally">Plump</span></li>
<li>Tee</li>
<li><span class="thai">ตี๋</span> <span class="tlit">dtĕe</span><span class="literally">(A light-skinned male of Chinese descent)</span></li>
</ul>
<ul>
<li>Toi</li>
<li><span class="thai">ต้อย</span> <span class="tlit">dtôi</span><span class="literally">Tiny</span></li>
</ul>
<ul>
<li>Uan</li>
<li><span class="thai">อ้วน</span> <span class="tlit">ûan</span><span class="literally">Fat</span></li>
<li>Neung</li>
<li><span class="thai">หนึ่ง</span> <span class="tlit">nèung</span><span class="literally">One</span></li>
</ul>
<ul>
<li>Yai</li>
<li><span class="thai">ใหญ่</span> <span class="tlit">yài</span><span class="literally">Big</span></li>
<li>Song</li>
<li><span class="thai">สอง</span> <span class="tlit">sŏng</span><span class="literally">Two</span></li>
</ul>
</div>
<p>Of course, how you look as a new-born baby and as a grown adult are completely different. The nickname often sticks though, even if it no longer has any meaning.</p>
<h2>Animals</h2>
<p>Another common source is names of animals, or even from the sounds animals make. This tradition is also related to protecting the newborn child from the spirits, as calling the baby as if it were an animal was thought to be an effective way of tricking the spirits into leaving it alone. </p>
<div class="nicknames-table">
<ul>
<li>Aod</li>
<li><span class="thai">อ๊อด</span><span class="tlit"> ót</span><span class="literally">Tadpole</span></li>
<li>Mot</li>
<li><span class="thai">มด</span><span class="tlit">mót</span><span class="literally">Ant</span></li>
</ul>
<ul>
<li>Aud</li>
<li><span class="thai">อู๊ด</span> <span class="tlit">óot</span><span class="literally">(From the sound of a pig)</span></li>
<li>Nok</li>
<li><span class="thai">นก</span><span class="tlit">nók </span><span class="literally">Bird</span></li>
</ul>
<ul>
<li>Hong</li>
<li><span class="thai">หงส์</span> <span class="tlit">hŏng</span><span class="literally">Swan</span></li>
<li>Non</li>
<li><span class="thai">หนอน</span><span class="tlit">nŏn </span><span class="literally">Worm</span></li>
</ul>
<ul>
<li>Jeab</li>
<li><span class="thai">เจี๊ยบ</span> <span class="tlit">jíap</span><span class="literally">Baby chicken</span></li>
<li>Nu</li>
<li><span class="thai">หนู</span> <span class="tlit"> nŏo </span><span class="literally">Mouse</span></li>
</ul>
<ul>
<li>Kai</li>
<li><span class="thai">ไก่</span> <span class="tlit">gài</span><span class="literally">Chicken</span></li>
<li>Pet</li>
<li><span class="thai">เป็ด</span> <span class="tlit">bpèt</span><span class="literally">Duck</span></li>
</ul>
<ul>
<li>Kob</li>
<li><span class="thai">กบ</span><span class="tlit"> gòp</span><span class="literally">Frog</span></li>
<li>Pla</li>
<li><span class="thai">ปลา</span> <span class="tlit">bplaa</span><span class="literally">Fish</span></li>
</ul>
<ul>
<li>Kung</li>
<li><span class="thai">กุ้ง</span> <span class="tlit">gûng</span><span class="literally">Shrimp</span></li>
<li>Poo</li>
<li><span class="thai">ปู</span><span class="tlit">bpoo</span><span class="literally">Crab</span></li>
</ul>
<ul>
<li>Maew</li>
<li><span class="thai">แมว</span> <span class="tlit">maew</span><span class="literally">Cat</span></li>
<li>Seua</li>
<li><span class="thai">เสือ</span> <span class="tlit">sĕua</span><span class="literally">Tiger</span></li>
</ul>
<ul>
<li>Mee</li>
<li><span class="thai">หมี</span> <span class="tlit">mĕe</span><span class="literally">Bear</span></li>
<li>Tai</li>
<li><span class="thai">ต่าย</span><span class="tlit">dtàai </span><span class="literally">(A shortened form of &#8220;Rabbit&#8221; </span><br />
<span class="thai">กระต่าย</span> <span class="tlit">gra-dtàai  </span></li>
</ul>
<ul>
<li>Meow</li>
<li><span class="thai">เหมียว</span> <span class="tlit">mĭeow </span><span class="literally">(From the sound of a cat)</span></li>
<li>Takkataen</li>
<li><span class="thai">ตั๊กแตน</span><span class="tlit">dták-gà-dtaen</span><span class="literally">Grasshopper</span></li>
</ul>
<ul>
<li>Moo</li>
<li><span class="thai">หมู</span><span class="tlit">mŏo</span><span class="literally">Pig</span></li>
<li>Tao</li>
<li><span class="thai">เต่า</span><span class="tlit">dtào</span><span class="literally">Turtle</span></li>
</ul>
</div>
<h2>Foreign Words</h2>
<p>English words also provide a fertile source for Thai nicknames. While some are taken straight from English names, like Jack ( <span class="tlit">jàek </span> ), others take inspiration from more unconventional sources. Say, food or technology&#8230;</p>
<div class="nicknames-table">
<ul>
<li>Apple</li>
<li><span class="thai">แอปเปิ้ล</span> <span class="tlit">àep-bpêrn</span></li>
<li>Bank</li>
<li><span class="thai">แบงค์</span> <span class="tlit">báeng</span></li>
</ul>
<ul>
<li>Beer</li>
<li><span class="thai">เบียร์</span><span class="tlit"> bia</span></li>
<li>Beam</li>
<li><span class="thai">บีม</span> <span class="tlit">beem</span></li>
</ul>
<ul>
<li>Cake</li>
<li><span class="thai">เค้ก</span><span class="tlit"> káyk</span></li>
<li>Benz</li>
<li><span class="thai">เบนซ์</span><span class="tlit"> bayn</span></li>
</ul>
<ul>
<li>Cream</li>
<li><span class="thai">ครีม</span><span class="tlit">kreem</span></li>
<li>Bomb</li>
<li><span class="thai">บอมบ์</span> <span class="tlit">bom&lt;/span&lt;</li>
</ul>
<ul>
<li>Ice</li>
<li><span class="thai">ไอซ์</span><span class="tlit">ai</span></li>
<li>Bonus</li>
<li><span class="thai">โบนัส</span><span class="tlit">boh-nát</span></li>
</ul>
<ul>
<li>Itim (from &#8220;ice cream&#8221;)</li>
<li><span class="thai">ไอติม</span><span class="tlit">ai-dtim</span></li>
<li>Fiat</li>
<li><span class="thai">เฟียต</span><span class="tlit">fîat</span></li>
</ul>
<ul>
<li>Jam</li>
<li><span class="thai">แยม</span><span class="tlit">yaem</span></li>
<li>Ford</li>
<li><span class="thai">ฟอร์ด</span><span class="tlit">fôt</span></li>
</ul>
<ul>
<li>Mint</li>
<li><span class="thai">มิ้นท์</span><span class="tlit">mín</span></li>
<li>Gear</li>
<li><span class="thai">เกียร์</span><span class="tlit">gia</span></li>
</ul>
<ul>
<li>Milk</li>
<li><span class="thai">มิลค์</span><span class="tlit">min</span></li>
<li>Gift</li>
<li><span class="thai">กิฟท์</span><span class="tlit">gìf</span></li>
</ul>
</div>
<p> &#8230;or leisure activities, or letters of the alphabet.</p>
<div class="nicknames-table">
<ul>
<li>Art</li>
<li><span class="thai">อาร์ต</span><span class="tlit"> áat</span></li>
<li>A / Ay</li>
<li><span class="thai">เอ</span><span class="tlit"> ay</span></li>
</ul>
<ul>
<li>Ball</li>
<li><span class="thai">บอล</span> <span class="tlit">bon</span></li>
<li>B / Bee</li>
<li><span class="thai">บี</span> <span class="tlit">bee</span></span></li>
</ul>
<ul>
<li>Cartoon</li>
<li><span class="thai">การ์ตูน</span><span class="tlit"> gaa-dtoon</span></li>
<li>J / Jay</li>
<li><span class="thai">เจ</span><span class="tlit">jay</span></li>
</ul>
<ul>
<li>Film</li>
<li><span class="thai">ฟิล์ม</span> <span class="tlit">fim</span></li>
<li>M / Em</li>
<li><span class="thai">เอ็ม</span><span class="tlit"> em</span></li>
</ul>
<ul>
<li>Game</li>
<li><span class="thai">เกม</span><span class="tlit"> gaym</span></li>
<li>O / Oh</li>
<li><span class="thai">โอ</span> <span class="tlit">oh</span></li>
</ul>
<ul>
<li>Golf</li>
<li><span class="thai">กอล์ฟ</span><span class="tlit"> góf</span></li>
<li>Q / Kiw</li>
<li><span class="thai">คิว</span><span class="tlit">kiw</span></li>
</ul>
<ul>
<li>Guitar</li>
<li><span class="thai">กีตาร์</span><span class="tlit"> gee-dtâa</span></li>
<li>R / Ah</li>
<li><span class="thai">อาร์</span><span class="tlit"> aa</span></li>
</ul>
<ul>
<li>Joke</li>
<li><span class="thai">โจ๊ก</span> <span class="tlit">jóhk</span></li>
<li>X / Ek</li>
<li><span class="thai">เอ็กส์</span><span class="tlit"> èk</span></li>
</ul>
<ul>
<li>Ping Pong</li>
<li><span class="thai">ปิงปอง</span> <span class="tlit">bping-bpong</span></li>
<li>Y / Wai</li>
<li><span class="thai">วาย</span><span class="tlit"> waai</span></li>
</ul>
</div>
<p>If some of the above seem unlikely choices for a name, others can be even more eccentric. It seems like any impressive-sounding foreign word is suitable for using as a nickname, so there are Thais out there with nicknames of &#8220;King Kong&#8221; <span class="thai">คิงคอง</span> <span class="tlit">king-kong</span> , &#8220;Laser&#8221; <span class="thai">เลเซอร์</span> <span class="tlit">lay-sêr</span> , &#8220;Vegas&#8221; <span class="thai">เวกัส</span> <span class="tlit">way-gàt</span> , &#8220;Chocolate&#8221; <span class="thai">ช็อคโกแลต</span> <span class="tlit">chók-goh-láet</span> , &#8220;Nasa&#8221; <span class="thai">นาซ่า</span> <span class="tlit">naa-sâa</span> and the like.</p>
<p>Most of the above names can be used for either gender, but most are more common for one gender than the other. The names of small animals and of food and drink are mostly female names, whereas names of leisure activities and technology are mainly male names. </p>
<p>Some people are actually named after their gender &#8211; Chai (<span class="thai">ชาย</span> <span class="tlit"> chaai </span>) &#8220;male&#8221;, Ying<br />
(<span class="thai">หญิง</span> <span class="tlit"> yĭng </span>) &#8220;female&#8221; and Num (<span class="thai">หนุ่ม</span> <span class="tlit"> nùm </span>) &#8220;young man&#8221; are all also common Thai nicknames. </p>
<p>Below are further examples of names that are either gender specific, or much more commonly used for that gender compared to the other.</p>
<h2>Female Nicknames</h2>
<div class="nicknames-table">
<ul>
<li>Chompoo</li>
<li><span class="thai">ชมพู่</span><span class="tlit"> chom-pôo</span><span class="literally"> Rose Apple</span></li>
<li>Nam</li>
<li><span class="thai">น้ำ</span><span class="tlit"> náam</span><span class="literally">Water </span></li>
</ul>
<ul>
<li>Dear</li>
<li><span class="thai">เดียร์</span><br />
<span class="tlit">dia</span><span class="literally"> (From English)</span></li>
<li>Noy Na</li>
<li><span class="thai">น้อยหน่า</span><span class="tlit"> nói-nàa</span><span class="literally">Sugar Apple</span></li>
</ul>
<ul>
<li>Dao</li>
<li><span class="thai">ดาว</span><br />
<span class="tlit">daao</span><span class="literally">Star</span></li>
<li>Ploy</li>
<li><span class="thai">พลอย</span><span class="tlit">ploi</span><span class="literally">Gem</span></li>
</ul>
<ul>
<li>Fah</li>
<li><span class="thai">ฟ้า</span><span class="tlit">fáa</span><span class="literally"> Sky</span></li>
<li>Praew</li>
<li><span class="thai">แพรว</span><span class="tlit">praew </span></span><span class="literally">Sparkling</span></li>
</ul>
<ul>
<li>Fai</li>
<li><span class="thai">ฝ้าย</span><br />
<span class="tlit">fâai</span><span class="literally"> Cotton</span></li>
<li>Sai</li>
<li><span class="thai">ทราย</span><span class="tlit">saai </span><span class="literally">Sand</span></li>
</ul>
<ul>
<li>Fon</li>
<li><span class="thai">ฝน</span><span class="tlit">fŏn </span><span class="literally">Rain</span></li>
<li>Tan</li>
<li><span class="thai">ตาล</span><span class="tlit">dtaan</span><span class="literally">Sugar palm </span></li>
</ul>
<ul>
<li>Kay</li>
<li><span class="thai">เก๋</span><br />
<span class="tlit">găy </span><span class="literally">Stylish</span></li>
<li>Tip</li>
<li><span class="thai">ทิพย์</span><span class="tlit">típ </span><span class="literally">Divine</span></li>
</ul>
<ul>
<li>Maem</li>
<li><span class="thai">แหม่ม</span><span class="tlit">màem </span><span class="literally">From English &#8220;Ma&#8217;am&#8221; </span></li>
<li>Tukta</li>
<li><span class="thai">ตุ๊กตา</span><span class="tlit">dtúk-(gà)-dtaa </span><span class="literally">Doll</span></li>
</ul>
</div>
<h2>Male Nicknames</h2>
<div class="nicknames-table">
<ul>
<li>Big</li>
<li><span class="thai">บิ๊ก</span> <span class="tlit">bík </span><span class="literally">(From English) </span></li>
<li>Pon</li>
<li><span class="thai">พล</span><span class="tlit"> pon</span><span class="literally">Armed forces</span></li>
</ul>
<ul>
<li>Boy</li>
<li><span class="thai">บอย</span> <span class="tlit">boi</span><span class="literally">(From English) </span></li>
<li>Pop</li>
<li><span class="thai">ป๊อป</span> <span class="tlit">bpóp</span><span class="literally">(From English)<br />
</span></li>
</ul>
<ul>
<li>Chai</li>
<li><span class="thai">ชัย</span> <span class="tlit">chai </span><span class="literally">Victory</span></li>
<li>Ton</li>
<li><span class="thai">ต้น</span><span class="tlit">dtôn </span><span class="literally">Leader</span></li>
</ul>
<ul>
<li>Ek</li>
<li><span class="thai">เอก</span><span class="tlit"> àyk </span><span class="literally">Main / most important </span></li>
<li>Tong</li>
<li><span class="thai">โต้ง</span><span class="tlit">dtôhng </span><span class="literally">Big</span></li>
</ul>
<ul>
<li>Jom</li>
<li><span class="thai">จอม</span> <span class="tlit">jom </span><span class="literally">Peak</span></li>
<li>Top</li>
<li><span class="thai">ท็อป</span><span class="tlit">tóp</span><span class="literally">(From English) </span></li>
</ul>
<ul>
<li>Kla</li>
<li><span class="thai">กล้า</span><span class="tlit">glâa </span><span class="literally">Brave</span></li>
<li>Wat</li>
<li><span class="thai">วัต</span><span class="tlit">wát </span><span class="literally">Religious duty</span></li>
</ul>
<ul>
<li>Pat</li>
<li><span class="thai">พัด</span><span class="tlit"> pát </span><span class="literally">Blowing wind</span></li>
<li>Yut</li>
<li><span class="thai">ยุทธ</span> <span class="tlit">yút</span><span class="literally"> Strategy</span></li>
</ul>
<ul>
<li>Pete</li>
<li><span class="thai">พีท </span><span class="tlit">pêet </span><span class="literally">(From English)</span></li>
</ul>
</div>
</div>
`,
   },
   {
      slug: 'personal-pronouns-in-thai',
      title: 'Personal pronouns in Thai',
      metaDescription: 'Personal pronouns in Thai',
      content: `<p>Certain aspects of Thai grammar are quite simple compared to other languages, but you can&#8217;t really say that about its complex personal pronoun structure. </p>
<p>Although you can get by with learning just a of the more common ones, a native Thai may switch personal pronouns many times a day depending on the situation, who they&#8217;re talking to and their age difference. The variety of pronouns range from the very formal to the very casual/insulting. There&#8217;s also distinct differences between male and female usage.</p>
<p>On top of that, several of the pronouns have more than one meaning and you&#8217;ll have to resort to context to work out the true meaning quite frequently.</p>
<p>Making things slightly easier is the fact that subject pronouns and object pronouns are the same (there&#8217;s no difference between &#8216;I&#8217; and &#8216;me&#8217; in Thai for instance) and that personal pronouns are frequently omitted from sentences altogether if they can be implied from the context.</p>
<p>Once you get used to the Thai way of speaking, you&#8217;ll find that simple pronouns tell you a great deal about the relationship between the speakers and their relative status. </p>
<h2>Polite Personal Pronouns</h2>
<p>The standard polite personal pronouns are: </p>
<div class="ctForExample">
<table cellspacing="0" cellpadding="0" border="0" width="100%">
<tbody>
<tr>
<td valign="top"><span class="thai">ผม</span> <span class="tlit"> pŏm </span><br />
I / me <i>(only used by men) </i></p>
<p><span class="thai">ฉัน</span> <span class="tlit">chăn</span><br />
I / me <i>(mainly used by women) </i></p>
<p><span class="thai">คุณ</span> <span class="tlit">kun</span><br />
You</td>
<td valign="top" class="exampleCellSeparator">
<span class="thai">เขา</span> <span class="tlit">kăo</span><br />
He / him / she / her</p>
<p><span class="thai">มัน</span>  <span class="tlit">man</span><br />
It </p>
<p><span class="thai">เรา</span>  <span class="tlit">rao</span><br />
We / us</p>
<p>(<span class="thai">พวก</span> ) <span class="thai">เขา</span> <span class="tlit">(pûak) kăo</span><br />
They / them</td>
</tr>
</tbody>
</table>
</div>
<p>If you&#8217;re just beginning to learn, then it&#8217;s safe to use just these words and you&#8217;ll never offend anybody. <span class="tlit"> pŏm </span> and <span class="tlit">kun</span> are somewhat formal words though, and as such may sound a bit out of place in casual and informal situations. </p>
<p><span class="tlit">kăo</span>, <span class="tlit">man</span> , <span class="tlit">rao</span>  and <span class="tlit">pûak kăo</span> are commonly used both in polite and informal  speech. For &#8220;they / them&#8221;, the <span class="tlit">pûak</span> is really only necessary if without it the context would be unclear, otherwise &#8220;they / them&#8221; can be translated just as <span class="tlit">kăo</span> the same as &#8220;he / she&#8221;. </p>
<p>For women, <span class="tlit">chăn </span> can be used in both polite and informal situations. Men also sometimes say <span class="tlit">chăn </span> , mostly when chatting with close friends or to their girlfriend or wife, but it&#8217;s a fair bit less common. A man saying <span class="tlit">chăn </span> excessively often or in inappropriate situations risks sounding somewhat effeminate too, so it&#8217;s best used with caution.</p>
<p>In casual speech, <span class="tlit">chăn</span> is commonly pronounced with a high tone as if spelt <span class="thai"><span class="thai">ชั้น</span> </span> <span class="tlit">chán</span> and <span class="tlit">kăo</span> as if spelt <span class="thai"><span class="thai">เค้า</span> </span> <span class="tlit">káo</span>.</p>
<h2>Informal Personal Pronouns </h2>
<div class="ctForExample">
<table cellspacing="0" cellpadding="0" border="0" width="100%">
<tbody>
<tr>
<td valign="top"> <span class="thai">เรา</span> <span class="tlit">rao</span><br />
I / me</p>
<p><span class="thai">นาย</span> <span class="tlit">naai</span><br />
You <i>(used by men and women, but only to refer to men) </i></td>
<td valign="top"> <span class="thai">เธอ</span> <span class="tlit">ter</span><br />
You ; She </p>
<p><span class="thai">หล่อน</span><br />
<span class="tlit">lòn</span><br />
She ; You<i> ( to refer to women) </i></td>
</tr>
</tbody>
</table>
</div>
<p>
<span class="tlit">ter</span> is an informal pronoun meaning &#8216;you&#8217;. It&#8217;s most commonly used when speaking male  female, and is much less often encountered in same-sex conversations. It&#8217;s also mainly only used when talking to someone of a similar age to you.</p>
<p><span class="tlit">chăn </span> and <span class="tlit">ter</span> are the pronouns of choice for the majority of Thai pop songs, regardless of whether it&#8217;s a man or woman singing. As an alternative to <span class="tlit">ter</span> when talking to men, either men or women can also use the similarly informal <span class="tlit">naai</span> to mean &#8216;you&#8217;.</p>
<p><span class="tlit">ter</span> also has a less common meaning of &#8216;she/her&#8217;. It&#8217;s a more formal word when used in this context, and occurs mostly in written Thai. <span class="tlit">rao</span> , the standard word for &#8216;we / us&#8217;, is confusingly enough also informally used as a pronoun for &#8216;I/me&#8217; too. </p>
<p>It&#8217;s mainly men who use it this way (as women have the alternative <b> </b><span class="tlit">chăn </span> ), and it&#8217;s frequently partnered with <span class="tlit">ter</span> or <span class="tlit">naai</span> for &#8216;you&#8217;. When <span class="tlit">rao</span> is intended to mean &#8216;we/us&#8217;, the sentence will often also contain the word <span class="thai"><span class="thai">กัน</span> </span> ( <span class="tlit">gan</span> , meaning &#8220;together&#8221;) to make the meaning clear.</p>
<p><span class="tlit">lòn</span> means &#8220;she&#8221;, or less commonly &#8220;you&#8221;, and is predominately used in written Thai. If used in spoken Thai, it can have something of a sarcastic or mildly derogatory connotation and so is best used with caution. </p>
<div class="ctForExample">
<table cellspacing="0" cellpadding="0" border="0" width="100%">
<tbody>
<tr>
<td valign="top"><span class="thai">ไอ</span> <span class="tlit">ai </span><br />
I / me</p>
<p><span class="thai">เค้า</span> <span class="tlit">káo</span><br />
I/me</td>
<td valign="top"><span class="thai">ยู</span> <span class="tlit">yoo</span><br />
You</p>
<p><span class="thai">ตัวเอง</span> <span class="tlit">dtua ayng</span><br />
You</td>
</tr>
</tbody>
</table>
</div>
<p>
<span class="tlit">ai </span> &#8216;I/me&#8217; and <span class="tlit">yoo</span> &#8216;you&#8217; are relatively recent additions that have been taken straight from their English equivalents, no doubt much to the chagrin of language purists. Another pairing, <span class="thai"><span class="thai">เค้า</span> </span> <span class="tlit">káo</span> &#8216;I/me&#8217; and <span class="thai"><span>ตัวเอง</span> </span></p>
<p><span class="tlit">dtua ayng</span> &#8216;you&#8217; is used similarly in casual situations. Both are most common amongst students and teenagers, and rarely used by those much older.</p>
<p>The use of <span class="tlit">káo</span> and <span class="tlit">dtua ayng</span> can be very confusing if you&#8217;re not expecting it, given that &lt;span class=&quot;thai&quot; <span class="thai">เค้า</span> </span> often sounds identical to <span class="thai"><span class="thai">เขา</span> </span></p>
<p><span class="tlit">kăo</span> ( the word for &#8220;he/she&#8221; as above) and the standard meaning of <span class="thai"><span>ตัวเอง</span> </span> <span class="tlit">dtua ayng</span> as &#8216;one&#8217;s self&#8217;.</p>
<h2>Informal / Offensive Pronouns</h2>
<div class="ctForExample">
<table cellspacing="0" cellpadding="0" border="0" width="100%">
<tbody>
<tr>
<td valign="top"><span class="thai">กู</span> <span class="tlit">goo </span><br />
I / me </p>
<p><span class="thai">มัน</span> <span class="tlit">man</span><br />
He / him / she / her</td>
<td valign="top"><span class="thai">มึง</span> <span class="tlit">meung</span><br />
You</p>
<p><span class="thai">แก</span> <span class="tlit">gae</span><br />
You ; He/She </p>
<p><span class="thai">เอ็ง</span> <span class="tlit">eng</span><br />
You</td>
</tr>
</tbody>
</table>
</div>
<p></p>
<p><span class="tlit">goo </span>, &#8216;I/me&#8217; , <span class="tlit">meung</span> , &#8216;you&#8217; and <span class="tlit">man</span> , &#8216;he/him/she/her&#8217; (though literally &#8216;it&#8217; as above) are often used by friends of many years when talking amongst and about each other. Used this way they serve as a kind of bonding term that shows close friendship, but be warned that using these terms outside of with close friends is considered very impolite and potentially abusive. </p>
<p>As such they can cause considerable offence if used inappropriately, and unless you&#8217;re very sure of your ground you&#8217;re best off avoiding using them altogether. <span class="tlit">gae</span> and <span class="tlit">eng</span>, both meaning &#8216;you&#8217;, are similar to <span class="tlit">meung</span> although marginally less impolite.</p>
<p><span class="tlit">gae</span> also has limited use as a third person pronoun too. When used this way, it&#8217;s not at all impolite but is used to refer in a respectful way to elders. Thai people referring to their parents in the third person will often use <span class="tlit">gae</span> for instance.</p>
<h2>Formal</h2>
<div class="ctForExample">
<table cellspacing="0" cellpadding="0" border="0" width="100%">
<tbody>
<tr>
<td valign="top"><span class="thai">กระผม</span>  <span class="tlit">grà-pŏm </span><br />
I / me <i>(only used by men)</i></p>
<p><span class="thai">ดิฉัน</span> <span class="tlit">dì-chăn </span><br />
I / me <i>(only used by women)</i>
</td>
<td valign="top"><span class="thai">ท่าน</span> <span class="tlit">tâan</span><br />
You</p>
<p><span class="thai">ข้าพเจ้า</span><br />
<span class="tlit">kâa-pá-jâo </span><br />
I / me </td>
</tr>
</tbody>
</table>
</div>
<p>Of these, you&#8217;re most likely to encounter <span class="tlit">tâan</span> as it&#8217;s commonly used by business when referring to customers in a general way. <span class="tlit">grà-pŏm</span> or <span class="tlit">dì-chăn </span> can be partnered with <span class="tlit">tâan</span> and used in very formal situations or where you want to be extra-polite.</p>
<p>You&#8217;re very unlikely to ever hear the even more formal <span class="tlit">kâa-pá-jâo</span> in normal speech, but may encounter it sometimes in official and formal documents (such as the Immigration form you fill in when entering Thailand).</p>
<h2>Plurals</h2>
<p>In the &#8220;Polite Personal Pronouns&#8221; section above, you can see <span class="tlit">kăo</span> as the word for &#8220;he / him / she / her&#8221; and <span class="tlit">pûak kăo</span> for &#8220;they / them&#8221;. The word <span class="thai"><span class="thai">พวก</span> </span> <span class="tlit">pûak</span> ( &#8220;group&#8221;) can actually be added in front of any of the other pronouns to make them plural as needed, so for instance you could say <span class="thai"><span class="thai">พวกผม</span> </span> <span class="tlit">pûak pŏm </span> for &#8220;us&#8221; or <span class="thai"><span class="thai">พวกท่าน</span> </span> <span class="tlit">pûak tâan </span> for &#8220;you&#8221; (in a plural sense).</p>
<h2>Nicknames and Titles </h2>
<p>In casual speech, it&#8217;s very common for women to avoid using a first-person pronoun altogether and instead refer to themselves in the 3rd person by using their nickname. For instance, someone called <span class="thai"><span class="thai">เหมียว</span></span> <span class="tlit">mĭeow</span> (usually written as &#8220;Meow&#8221; ) would say the equivalent of &#8220;Meow is tired&#8221; rather than &#8220;I am tired&#8221;. Similarly, they can also be referred to using their nickname instead of a second-person or third-person pronoun (e.g. &#8220;Is Meow tired?&#8221; rather than &#8220;Are you tired?&#8221;). </p>
<p>Though this may take a bit of getting used to at first, it&#8217;s a useful way of addressing someone if you&#8217;re not sure which of the various pronouns is most appropriate.</p>
<p>While it&#8217;s not unknown for men to also refer to themselves this way on occasion, it&#8217;s much less common and, as with using <span class="tlit">chăn </span>, may sound quite effeminate unless you&#8217;re sure of your ground.</p>
<p>When addressing someone who has or works in a respected position, it&#8217;s usually<br />
respectful and appropriate to use their title rather than any of the above pronouns. So students will refer to their teacher as <span class="thai"><span class="thai">อาจารย์</span> </span> <span class="tlit">aa-jaan</span> (the word for &#8220;teacher&#8221;) and patients or staff to their doctor as <span class="thai"><span class="thai">คุณหมอ</span> </span> <span class="tlit">kun mŏr</span> (the word for &#8220;doctor&#8221;).</p>
<h2>Others</h2>
<p>Apart from the use of family terms as pronouns, there are other personal pronouns not covered here but which are unlikely to be frequently encountered. For instance, there&#8217;s special pronouns to be used when talking to monks, a lot more in the Thai royal language and the main regional dialects of Thai also have their own pronouns. Ethnic Chinese Thais also have some separate pronouns derived from various Chinese dialects.</p>
`,
   },
   {
      slug: 'are-thais-all-one-big-happy-family',
      title: 'Are Thais all one big happy family?',
      metaDescription: 'Are Thais all one big happy family?',
      content: `<p>In addition to the standard personal pronouns, it&#8217;s very common when speaking Thai to also use family terms such as &#8220;brother&#8221;, &#8220;sister&#8221;, &#8220;aunt&#8221;, &#8220;uncle&#8221;, &#8220;mother&#8221; and &#8220;father&#8221; as personal pronouns. This applies even to strangers that you have no blood relation to at all.</p>
<h2>Brothers and Sisters</h2>
<p>The Thai language has no direct translation for &#8220;brother&#8221; or &#8220;sister&#8221;; instead the term used depends on your relative ages.</p>
<div class="ctForExample">
<table cellspacing="0" cellpadding="0" border="0" width="100%">
<tbody>
<tr>
<td valign="top"><span class="thai">พี่</span> <span class="tlit"> pêe </span><br />
Older brother or sister</p>
<p><span class="thai">พี่ชาย</span> <span class="tlit">pêe chaai </span><br />
Older brother</p>
<p><span class="thai">พี่สาว</span> <span class="tlit">pêe săao</span><br />
Older sister
</td>
<td valign="top"><span class="thai">น้อง</span>               <span class="tlit">nóng</span><br />
Younger brother or sister </p>
<p><span class="thai">น้องชาย</span> <span class="tlit">nóng chaai</span><br />
Younger brother</p>
<p><span class="thai">น้องสาว</span> <span class="tlit">nóng săao</span><br />
Younger sister </td>
</tr>
</tbody>
</table>
</div>
<p>Of these, the general forms  <span class="tlit"> pêe </span> and   <span class="tlit">nóng</span>  are  used as  personal pronouns and are also used as titles before someone&#8217;s name, though the way they are used is somewhat different.</p>
<p><span class="tlit"> pêe </span> is by far the more common, and is  used with people who are (very roughly) up to  15-20 years older than you. It&#8217;s considered to be a term that shows respect, and so people will often use it even if they&#8217;re not sure if the person they&#8217;re talking to is really older than them or not. </p>
<p>When speaking to, or referring to, older  males, both men and women commonly use  <span class="tlit"> pêe </span> as a title. So for instance, a man called <span class="thai">ต้น</span> ( <span class="tlit"> dtôn </span>, usually written as &#8220;Ton&#8221;)  would often be called <span class="thai">่ต้น</span> ( <span class="tlit"> pêe dtôn </span>, which would usually written as &#8220;P&#8217; Ton&#8221;) by younger friends, junior office colleagues, a (younger) girlfriend or wife etc. </p>
<p>If the context made clear who was being talked about, this could be shortened to just <span class="tlit"> pêe </span>. Similarly, he may choose to refer to himself as   <span class="tlit"> pêe </span> instead of using a standard personal pronoun. The same rules apply when talking  or referring to older women too, although it&#8217;s a bit less common.</p>
<p><span class="tlit">nóng</span>  can be used in a similar way, although it should be used somewhat with caution.  As  <span class="tlit"> pêe </span> implies respect, the  opposite  <span class="tlit">nóng</span>  could potentially imply inexperience or lack of status, and so be  a condescending form of address to someone who views themselves of equal status. </p>
<p>Because of this, it tends to be used when there&#8217;s a very clear age difference (5+ years) or when the relationship between the parties is clearly defined, which ensures there&#8217;s little danger of a misunderstanding. If used correctly, it&#8217;s  viewed as a friendly and polite form of address and not uncommonly used a boss and his/her younger employees, or a older man to a younger girlfriend/wife. One of its other most  common other uses is when calling to an obviously younger waiter/waitress in a restaurant.</p>
<p>Thais speaking English may translate  <span class="tlit"> pêe </span> and   <span class="tlit">nóng</span>  as &#8220;brother&#8221; or &#8220;sister&#8221; regardless of the actual relationship,  so be aware that someone introduced as a &#8220;brother&#8221; for instance may in reality not be a genuine brother, but perhaps a cousin, a friend or even boyfriend or husband.</p>
<h2>Aunts and Uncles </h2>
<div class="ctForExample">
<table cellspacing="0" cellpadding="0" border="0" width="100%">
<tbody>
<tr>
<td valign="top"><span class="thai">ลุง</span> <span class="tlit"> lung </span><br />
Uncle   </p>
<p><span class="thai">อา</span> <span class="tlit">aa </span><br />
Uncle / Aunt</td>
<td valign="top"> <span class="thai">ป้า</span> <span class="tlit">bpâa</span><br />
Aunt</p>
<p><span class="thai">น้า</span> <span class="tlit">náa</span><br />
Uncle / Aunt </td>
</tr>
</tbody>
</table>
</div>
<p>&#8220;Aunt&#8221; and &#8220;Uncle&#8221; also don&#8217;t have direct translations in Thai. The closest are  <span class="tlit"> lung </span>, which refers to an elder brother of either your mother or father, and  <span class="tlit">bpâa</span>  which refers to an elder sister. <span class="tlit">aa </span> refers to either a younger brother or younger sister of your mother, and so can be either &#8220;Uncle&#8221; or &#8220;Aunt&#8221;.  </p>
<p><span class="tlit">náa</span>  is the equivalent on the father&#8217;s side. Both  <span class="tlit"> lung </span> and  <span class="tlit">bpâa</span>  are commonly used as personal pronouns in much the same way as  <span class="tlit"> pêe </span>, except for talking to someone much older than you. </p>
<p>The general rule is that if you feel they&#8217;re in the same generation as you, use   <span class="tlit"> pêe </span> or if you think they&#8217;re in your parents generation use  <span class="tlit"> lung </span> or  <span class="tlit">bpâa</span>  instead. </p>
<h2>Parents and Children </h2>
<div class="ctForExample">
<table cellspacing="0" cellpadding="0" border="0" width="100%">
<tbody>
<tr>
<td valign="top"><span class="thai">พ่อ</span> <span class="tlit">pôr </span><br />
Father</p>
<p><span class="thai">แม่</span> <span class="tlit">mâe</span><br />
Mother</p>
<p><span class="thai">พ่อแม่</span> <span class="tlit">pôr mâe </span><br />
Parents
</td>
<td valign="top"><span class="thai">ลูก</span> <span class="tlit">lôok</span><br />
Child </p>
<p><span class="thai">ลูกชาย</span> <span class="tlit">lôok chaai </span><br />
Son</p>
<p><span class="thai">ลูกสาว</span> <span class="tlit">lôok săao </span><br />
Daughter</p>
<p><span class="thai">หนู</span> <span class="tlit">nŏo</span><br />
<i>(pronoun used by children) </i></td>
</tr>
</tbody>
</table>
</div>
<p><span class="tlit">pôr </span> and  <span class="tlit">mâe</span>  are both very commonly used as personal pronouns when parents speak to their children. This is to such an extent that a married couple with children may start to refer to each other as  <span class="tlit">pôr </span> and  <span class="tlit">mâe</span> , even when their children aren&#8217;t about. </p>
<p>Parents will tend to refer to their child as  <span class="tlit">lôok</span>  rather than using any of the second-person pronouns, and the child will refer to themselves as  <span class="tlit">nŏo</span>  .   <span class="tlit">nŏo</span>  (literally meaning &#8220;mouse&#8221;) is used by both young boys and girls, although boys stop doing so before they&#8217;re teenagers and start saying  <span class="tlit">pŏm</span>  instead. </p>
<p>Women on the other hand may refer to themselves as <span class="tlit">nŏo</span> when speaking to their parents or someone much older for pretty much as long they want to. </p>
<p>If being introduced to a friend or partner&#8217;s parents, it&#8217;s quite normal and polite to refer to them as <span class="thai">คุณพ่อ</span>   <span class="tlit">kun pôr </span> and <span class="thai">คุณแม่</span>  <span class="tlit">kun mâe</span> , as if they were your own parents.</p>
<h2>Grandparents </h2>
<div class="ctForExample">
<table cellspacing="0" cellpadding="0" border="0" width="100%">
<tbody>
<tr>
<td valign="top"><span class="thai">ปู่</span> <span class="tlit">bpòo </span><br />
Grandfather (<i>paternal</i>)                </p>
<p><span class="thai">ตา</span> <span class="tlit">dtaa</span><br />
Grandfather (<i>maternal</i>) </td>
<td valign="top"><span class="thai">ย่า</span> <span class="tlit">yâa</span><br />
Grandmother (<i>paternal</i>) </p>
<p><span class="thai">าย</span> <span class="tlit">yaai</span><br />
Grandmother (<i>maternal</i>) </td>
</tr>
</tbody>
</table>
</div>
<p>Any of the above terms for grandparents can also be used as personal pronouns where appropriate. In addition, they can be used when talking or referring to unrelated people who appear to be of a similar age to your grandparents, a usage  in the same vein as    <span class="tlit"> pêe </span> ,  <span class="tlit"> lung </span> or  <span class="tlit">bpâa</span>  as described above. Clearly, unrelated people are not going to have any paternal or maternal connection, so either option can be used in that situation.  </p>
<h2>In-Laws &amp; Step-family</h2>
<div class="ctForExample">
<table cellspacing="0" cellpadding="0" border="0" width="100%">
<tbody>
<tr>
<td valign="top"><span class="thai">พ่อตา</span> <span class="tlit">pôr dtaa </span><br />
Father-in-law</p>
<p><span class="thai">กเขย</span> <span class="tlit">lôok kŏie</span><br />
Son-in-law </p>
<p><span class="thai">พี่เขย</span> <span class="tlit">pêe kŏie</span><br />
(Older) Brother-in-law </p>
<p><span class="thai">น้องเขย</span> <span class="tlit">nóng kŏie</span><br />
(Younger) Brother-in-law </p>
<p><span class="thai">พ่อเลี้ยง</span> <span class="tlit">pôr líang </span><br />
Step-father</p>
<p><span class="thai">ลูกติด</span> <span class="tlit">lôok-dtìt</span><br />
Step-son / step-daughter </td>
<td valign="top"><span class="thai">แม่ยาย</span> <span class="tlit">mâe yaai</span><br />
Mother-in-law</p>
<p><span class="thai">ลูกสะใภ้</span>   <span class="tlit">lôok sà-pái</span><br />
Daughter-in-law </p>
<p><span class="thai">พี่สะใภ้</span> <span class="tlit">pêe sà-pái </span><br />
(Older) Sister-in-law </p>
<p><span class="thai">น้องสะใภ้</span> <span class="tlit">nóng-sà-pái </span><br />
(Younger) Sister-in-law </p>
<p><span class="thai">แม่เลี้ยง</span> <span class="tlit">mâe líang</span><br />
Step-mother</td>
</tr>
</tbody>
</table>
</div>
<p>All of these are  pretty much only used as nouns  as in English. Step-brothers and sisters will most commonly refer to each other as   <span class="tlit"> pêe </span> and   <span class="tlit">nóng</span> , just like full brothers and sisters.</p>
`,
   },
   {
      slug: 'thai-dictionary-designed-for-learners-of-thai',
      title: 'Thai dictionary, designed for learners of Thai',
      metaDescription:
         'Comprehensive Thai - English dictionary with 100k+ words, thousands of example sentences, sound files and much more.',
      content: `<div class="thai-english-dictionary">
A dictionary is a basic essential for any language learner. For Thai though, the alphabet presents an immediate, and considerable, obstacle.</p>
<p>Looking up an English word ? You&#8217;ll get a bunch of Thai script to read in return. If you look up a Thai word, you may get a nice English definition, but you&#8217;ll still need to be able to spell and type Thai first. No problem for Thais, not so good for the rest of us.</p>
<p>The Thai2English dictionary is different. It&#8217;s a full <em>Thai &gt; English</em> and <em>English &gt; Thai</em> dictionary that you&#8217;d expect, but you don&#8217;t need any Thai script knowledge to use it. All Thai words are shown using our <a href="/learn/thai-transliteration-the-good-the-bad-and-the-ugly">transliteration</a> along with <a href="/learn/what-are-tone-markers">tone markers</a>.</p>
<p>You can use transliteration to search too &#8211; just type what the word sounds like to you, and we&#8217;ll do our best to find it.</p>
<p>We also show all words in Thai script too of course, so if you&#8217;re more comfortable with that we&#8217;ve got you covered.</p>
<p> <img loading="lazy" class="logo desktop-only" src="/img/dictionary-meanings@2x.png" width="710" height="504" /><br />
<img class="logo mobile-only" src="/img/dictionary-page2-mobile2.png" /></p>
<p>The dictionary contains hundreds of thousands of Thai and English words, along with sound files, phonetic spellings and usage information where appropriate.</p>
<p>As longer Thai words are usually built up using smaller component words, we show both component and examples so you can better understand how it all fits together. </p>
<p> <img class="sentences desktop-only" src="/img/dictionary-sentences@2x.png" /><br />
<img class="sentences mobile-only" src="/img/dictionary-page3-mobile.png" /></p>
<p>For extra help, there are tens of thousands of example sentences like these ones available in both Thai and English. These help a lot in understanding how to use a word in real world contexts. </p>
<p>Ready to get started? The search box is right there at the <a href="#">top of the page</a>&#8230;</p>
</div>
`,
   },
]

export const getBlogPost = (slug: string): BlogPost | undefined =>
   blogPosts.find((p) => p.slug === slug)
